/**
 * File-name conflict detection & confirmation helpers used by the create
 * flow. Kept separate so `files-crud.ts` and `files-save.ts` don't grow the
 * confirmation-dock plumbing.
 */

import { closeDock, enableSave, setConfirmationDock } from "@actions/global";
import { getStore } from "@store";
import {
  getFileManagerAdapter,
  type Store,
} from "./files-helpers";
import type { FileThunk } from "./files-init";

export const getIndividualFileLocation = async (
  location_name: string,
  store: Store = getStore()
): Promise<any> => {
  const filesAdapter = await getFileManagerAdapter(store);
  const file_location =
    await filesAdapter.fetchIndividualFileLocation(location_name);
  const { file_status } = file_location.length > 0 ? file_location[0] : {};
  return file_status;
};

/**
 * If `fileName` already exists in a Live or Scheduled state at `fileLocation`,
 * opens a confirmation dock warning the user that saving will overwrite it.
 * No-ops when the file name is unique.
 */
export const checkForExistingFileName = (
  fileLocation: string,
  fileName: string
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  const filesAdapter = await getFileManagerAdapter(store);
  if (fileLocation) {
    const filesMatched = await filesAdapter.fetchFileBucketLocation(
      fileLocation,
      fileName
    );
    if (
      filesMatched.file_exist &&
      (filesMatched.exist_in?.includes("Live") ||
        filesMatched.exist_in?.includes("Scheduled"))
    ) {
      const storeContents = store.getState();
      store.dispatch(
        setConfirmationDock({
          title: "This File Name Already Exists",
          message: `File Name '${fileName}' already exists at this location. Uploading this file will overwrite '${fileName}' on its Scheduled Date. Confirm below if you'd like to proceed, or cancel and upload a file with a different name. Please Note: A new file name will generate a unique URL where the file is hosted.`,
          updateAction: "overwriteFile",
          idTarget: storeContents.fileNewView.file_id,
        })
      );
    }
  }
};

/** Confirmation-dock callback for the file-overwrite flow. */
export const confirmOverwrite = (
  _fileId: any,
  _action: string
): FileThunk => async (dispatch, _getState) => {
  dispatch(enableSave());
  dispatch(closeDock());
};
